import { environment } from "src/environments/environment";
import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { NavigationEnd, Router } from "@angular/router";
import { filter, takeUntil, Subject } from "rxjs";
import { finalize, switchMap, take } from "rxjs/operators";
import { MenuComponent } from "src/app/_metronic/kt/components";
import { AuthService } from "src/app/pages/auth";
import { LocalStorageService } from "src/app/services/local-storage-service/local-storage.service";
import { RoleModel } from "src/app/services/models/user-management/user-management.model";
import { ToastService } from "src/app/services/toast-service/toast.service";


@Component({
  selector: "app-navbar",
  templateUrl: "./navbar.component.html",
  styleUrls: ["./navbar.component.scss"],
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class NavbarComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() appHeaderDefaulMenuDisplay: boolean;
  @Input() isRtl: boolean;

  itemClass = "ms-1 ms-lg-3";
  btnClass =
    "btn btn-icon btn-custom btn-icon-muted btn-active-light btn-active-color-primary w-35px h-35px w-md-40px h-md-40px";
  userAvatarClass = "symbol-35px symbol-md-40px";
  btnIconClass = "fs-2 fs-md-1";

  toggleCounter = 0;
  isSwitchingRole = false;

  environment = environment;
  is_destroyed = false;

  // Hide global filter on dashboard page
  showGlobalFilter = signal<boolean>(true);

  #auth = inject(AuthService);
  #localStorageService = inject(LocalStorageService);
  #router = inject(Router);
  #toastService = inject(ToastService);
  #destroy$ = new Subject<void>();

  constructor() {
    this.checkRoute(this.#router.url);
  }

  get currentUser() {
    return this.#auth.currentUser;
  }

  get displayName(): string {
    const fullName = this.getRoleLabel(this.activeRole ?? this.availableRoles[0]);

    if (typeof fullName === "string" && fullName.length > 0) {
      return fullName;
    }

    return "แอดมิน";
  }

  get activeRole(): RoleModel | null {
    return this.currentUser()?.active_role ?? this.#auth.getSelectedRole();
  }

  get activeRoleId(): number | null {
    const roleId = Number(this.activeRole?.id);

    return Number.isFinite(roleId) ? roleId : null;
  }

  get availableRoles(): RoleModel[] {
    const currentUser = this.currentUser();
    const roles = currentUser?.user.roles ?? currentUser?.roles ?? [];
    const uniqueRoles = new Map<number, RoleModel>();

    roles.forEach((role) => {
      const roleId = Number(role.id);

      if (!Number.isFinite(roleId) || uniqueRoles.has(roleId)) {
        return;
      }

      uniqueRoles.set(roleId, role);
    });

    return Array.from(uniqueRoles.values());
  }

  get canSwitchRole(): boolean {
    return this.availableRoles.length > 1;
  }

  ngOnDestroy(): void {
    MenuComponent.reinitialization();
    this.#localStorageService.setItem("navbar-destroy", "true");
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  ngAfterViewInit(): void {
    MenuComponent.reinitialization();
    setTimeout(() => {
      this.triggerClick();
    }, 200); //delay to click empty for first init component
  }

  triggerClick(): void {
    const nav_item = document.getElementById("navbar-menu-trigger");
    if (nav_item) {
      nav_item.click();
    }
  }

  ngOnInit(): void {
    this.is_destroyed =
      this.#localStorageService.getItem("navbar-destroy") === "true";
    MenuComponent.reinitialization();
    this.#localStorageService.removeItem("navbar-destroy");
    this.#localStorageService.removeItem("user-menu");
    // this.updateCart();

    // Listen for route changes to show/hide global filter
    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.#destroy$),
      )
      .subscribe((event) => {
        this.checkRoute((event).url);
      });
  }

  updateCart(): void {}

  logout(): void {
    this.#auth.logout();
  }

  onRoleChange(role: RoleModel): void {
    const selectedRoleId = Number(role.id);
    const previousRoleId = this.activeRoleId;

    if (
      this.isSwitchingRole ||
      !Number.isFinite(selectedRoleId) ||
      selectedRoleId === previousRoleId ||
      !this.availableRoles.some((role) => Number(role.id) === selectedRoleId)
    ) {
      return;
    }

    this.isSwitchingRole = true;
    this.#auth.switchRole(selectedRoleId).pipe(
      switchMap(() => this.#auth.restoreSession()),
      take(1),
      finalize(() => {
        this.isSwitchingRole = false;
      }),
    ).subscribe({
      next: (session) => {
        MenuComponent.reinitialization();
        const targetUrl = this.#auth.getDefaultAuthenticatedUrl(session);
        void this.navigateAfterRoleSwitch(targetUrl);
      },
      error: (err: unknown) => {
        this.#toastService.toastError(this.extractRoleSwitchErrorMessage(err));
      },
    });
  }

  _setUserToggle(e: Event): void {
    if (this.toggleCounter === 0) {
      const menu_element = document.getElementById(
        "user-dropdown",
      ) as HTMLElement;
      const menu = MenuComponent.getInstance(menu_element) as MenuComponent;
      //Delay click for MenuComponent call .click 2 times
      setTimeout(() => {
        menu.click(menu_element, e);
      }, 0);
      setTimeout(() => {
        //Delay click when user click other btn
        if (this.#localStorageService.getItem("user-menu") === "hide") {
          menu.click(menu_element, e);
        }
      }, 20);
      if (this.is_destroyed) {
        //Delay click when navbar destroy
        setTimeout(() => {
          menu.click(menu_element, e);
        }, 0);
      }
      this.toggleCounter += 1;
    }
  }

  private checkRoute(url: string): void {
    // Hide global filter on dashboard page
    this.showGlobalFilter.set(!url.includes("/dashboard"));
  }

  private getRoleLabel(role: RoleModel | null | undefined): string {
    return String(role?.name_th ?? role?.label ?? role?.name ?? "").trim();
  }

  private async navigateAfterRoleSwitch(targetUrl: string): Promise<void> {
    const currentPath = this.#router.url.split("?")[0].split("#")[0];

    if (currentPath !== targetUrl) {
      await this.#router.navigateByUrl(targetUrl);
      return;
    }

    const navigated = await this.#router.navigateByUrl(targetUrl);

    if (!navigated) {
      window.location.assign(targetUrl);
    }
  }

  private extractRoleSwitchErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const error = err.error;

      if (typeof error === "string" && error.trim() !== "") {
        return error;
      }

      if (error != null && typeof error === "object") {
        const errors = (error as { errors?: unknown }).errors;
        const message = (error as { message?: unknown }).message;

        if (Array.isArray(errors) && errors.length > 0) {
          return errors.join("\n");
        }

        if (typeof errors === "string" && errors.trim() !== "") {
          return errors;
        }

        if (typeof message === "string" && message.trim() !== "") {
          return message;
        }
      }
    }

    return "ไม่สามารถเปลี่ยนสิทธิ์การใช้งานได้ กรุณาลองใหม่อีกครั้ง";
  }
}
